import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { message, Button, Popover } from 'antd';
import get from 'lodash/get';
import Loading from '$app/shared/components/Loading';
import { BottomDiv, GarageSearch, StyledModal } from '../../styled';
import { GetAssociationsByExternalIdRequest } from '../actions';
import { createJobInvitation as createJobInvitationRequest } from '../../api';
import GarageTable from './GarageTable';

const getErrorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;
import {
  isCustomerCD,
  displayCollectionDeliveryOption,
  filterGaragesBySupportedWeight,
  filterGaragesByExistingInvitations,
  filterGaragesBySearchInput,
  sortGaragesAlphabetically,
  operationsRateExist,
  getRelevantSpecialitiesForBooking,
} from './utils';
import ChangeCndOptions from './ChangeCndOptions';
import OutOfHoursWarning from './OutOfHoursWarning';
import BookingPageContext from '../../../context';

const CUSTOMERCD_GARAGE_RADIUS = 30;

const applyGarageFilters = ({ garages, vehicleWeight, existingGarageInvites, searchInput }) => {
  const garagesThatSupportVehicleWeight = filterGaragesBySupportedWeight(garages, vehicleWeight);
  const garagesWithoutExistingInvites = filterGaragesByExistingInvitations(
    garagesThatSupportVehicleWeight,
    existingGarageInvites
  );
  const garagesMatchingSearchTerms = filterGaragesBySearchInput(garagesWithoutExistingInvites, searchInput);
  return sortGaragesAlphabetically(garagesMatchingSearchTerms);
};

const addWorkOperationSupportFlag = (garages, capableGarages) => {
  const garageCapabilityRecords = Object.keys(capableGarages);

  if (!garageCapabilityRecords.length) return garages;

  return garages.map((garage) => {
    const garageIsNotCapable = !capableGarages[garage.garageId];
    if (garageIsNotCapable) garage.canPerformAllWorkForBooking = false;
    return garage;
  });
};

// Deliberately does NOT filter by booking.garageId. The legacy selector in the
// old redux store attempted that filter but read garageId from the wrong state
// slice, so in practice all Customer C&D garages within the radius were shown.
// We preserve that behavior here so bookings with an assigned garage still see
// the full list of Customer C&D options.
const getCustomerCndGarages = (garages) =>
  garages.filter(({ collectionRoundedMiles }) => collectionRoundedMiles <= CUSTOMERCD_GARAGE_RADIUS);

const getNonCustomerCndGarages = (garages) =>
  garages.filter(({ deliveryOptions }) =>
    deliveryOptions.find(({ deliveryOption }) => deliveryOption !== 'CustomerC&D')
  );

function GaragePickerModal({ booking, garages, checkPopupOpen, closePopup, loadingGarages }) {
  const { reloadBooking } = useContext(BookingPageContext);
  const [state, setState] = useState({
    collectionDeliveryOption: booking.collectionDeliveryOption,
    showWarning: false,
    warningMessage: null,
    count: 0,
    capableGarages: {},
    specialityCapabilitiesStatus: 'loading',
    garageNameSearchInput: '',
    timetableRules: {},
    showCollectionTypeChangeWarning: false,
  });

  const customerCndGarages = useMemo(() => getCustomerCndGarages(garages), [garages]);
  const nonCustomerCndGarages = useMemo(() => getNonCustomerCndGarages(garages), [garages]);
  const bookingOperationSkusKey = useMemo(
    () =>
      JSON.stringify(
        get(booking, 'workOperations', [])
          .map((wo) => wo.sku)
          .filter((sku) => sku && !sku.includes('c&d'))
          .filter(Boolean)
      ),
    [booking.workOperations]
  );
  const collectionTime = booking.collectionDateTimeLocal
    ? moment(booking.collectionDateTimeLocal).format('HH:mm')
    : null;
  const deliveryTime = booking.deliveryDateTimeLocal ? moment(booking.deliveryDateTimeLocal).format('HH:mm') : null;
  const collectionDeliveryOption = booking.collectionDeliveryOption;

  const onGarageRowClick = (garage) => {
    setState((st) => ({
      ...st,
      garageId: garage.garageId,
      garage,
      collectionDeliveryOption: displayCollectionDeliveryOption(garage, collectionDeliveryOption),
    }));
  };

  useEffect(() => {
    if (!checkPopupOpen) {
      return undefined;
    }

    let isActive = true;
    const bookingOperationSkusForRequest = JSON.parse(bookingOperationSkusKey);

    const degradeSpecialityCapabilitiesLoad = (warningMessage, warningDetails) => {
      if (warningDetails !== undefined) {
        console.warn(warningMessage, warningDetails);
      } else {
        console.warn(warningMessage);
      }
      message.error('Unable to load garage work operation capabilities');
      // Allow garage selection without capability tags rather than blocking allocation.
      setState((st) => ({ ...st, capableGarages: {}, specialityCapabilitiesStatus: 'ready' }));
    };

    async function resolveGarageSpecialityCapabilities() {
      if (!bookingOperationSkusForRequest.length) {
        if (!isActive) return;
        setState((st) => ({ ...st, capableGarages: {}, specialityCapabilitiesStatus: 'ready' }));
        return;
      }

      if (!isActive) return;
      setState((st) => ({ ...st, capableGarages: {}, specialityCapabilitiesStatus: 'loading' }));

      try {
        const garageSpecialitiesBySku = await GetAssociationsByExternalIdRequest(
          bookingOperationSkusForRequest
        );

        if (!isActive) return;

        if (!Array.isArray(garageSpecialitiesBySku)) {
          degradeSpecialityCapabilitiesLoad(
            'GaragePickerModal: GetAssociationsByExternalIdRequest returned unexpected data:',
            garageSpecialitiesBySku
          );
          return;
        }

        const { capabilitiesPerGarage, validSkuCount, hadMalformedEntries } =
          getRelevantSpecialitiesForBooking(garageSpecialitiesBySku);

        if (hadMalformedEntries) {
          degradeSpecialityCapabilitiesLoad(
            'GaragePickerModal: speciality response contained malformed entries that were excluded'
          );
          return;
        }

        if (validSkuCount === 0 || validSkuCount !== bookingOperationSkusForRequest.length) {
          degradeSpecialityCapabilitiesLoad(
            `GaragePickerModal: expected ${bookingOperationSkusForRequest.length} speciality entries, received ${validSkuCount} valid entries`
          );
          return;
        }

        if (!Object.keys(capabilitiesPerGarage).length) {
          degradeSpecialityCapabilitiesLoad(
            'GaragePickerModal: no garage speciality associations found in response'
          );
          return;
        }

        const capableGarages = {};
        Object.keys(capabilitiesPerGarage).forEach((garageId) => {
          capableGarages[garageId] =
            capabilitiesPerGarage[garageId] === bookingOperationSkusForRequest.length;
        });

        setState((st) => ({ ...st, capableGarages, specialityCapabilitiesStatus: 'ready' }));
      } catch (error) {
        if (!isActive) return;
        console.warn('GaragePickerModal: failed to resolve garage speciality capabilities', error);
        message.error(getErrorMessage(error, 'Unable to load garage work operation capabilities'));
        setState((st) => ({ ...st, capableGarages: {}, specialityCapabilitiesStatus: 'ready' }));
      }
    }

    resolveGarageSpecialityCapabilities();

    return () => {
      isActive = false;
    };
  }, [bookingOperationSkusKey, checkPopupOpen]);

  useEffect(() => {
    if (!checkPopupOpen) {
      setState((st) => ({
        ...st,
        garageId: undefined,
        garage: undefined,
        garageNameSearchInput: '',
        collectionDeliveryOption: booking.collectionDeliveryOption,
        capableGarages: {},
        specialityCapabilitiesStatus: 'loading',
      }));
    }
  }, [booking.collectionDeliveryOption, checkPopupOpen]);

  const handleOk = async () => {
    if (!state.garageId) {
      message.error('You must select a garage');
    } else if (!state.collectionDeliveryOption) {
      message.error('You need to set a C&D option');
    } else if (!state.garage.operations || !operationsRateExist(state.garage.operations)) {
      message.error('Selected garage must have updated Labour rates');
    } else {
      try {
        await createJobInvitationRequest(booking.bookingId, {
          status: 'draft',
          priceWithVat: 0,
          priceWithoutVat: 0,
          garageId: state.garageId,
          collectionDeliveryOption: state.collectionDeliveryOption,
        });
        closePopup();
        reloadBooking();
        return;
      } catch (error) {
        message.error(getErrorMessage(error, 'Unable to create draft job invite'));
      }
    }

    setState((st) => ({ ...st, showWarning: false, warningMessage: null }));
  };

  const onCancelled = () => {
    closePopup();
    setState((st) => ({ ...st, showWarning: false, count: 0, warningMessage: null }));
  };

  const changeCndType = (e) => {
    setState((st) => ({
      ...st,
      showCollectionTypeChangeWarning: collectionDeliveryOption !== e.target.value,
      collectionDeliveryOption: e.target.value,
      showWarning: !['Fixter', 'CustomerC&D'].includes(state.collectionDeliveryOption),
    }));
  };

  const loadMoreGarages = () => setState((st) => ({ ...st, count: st.count + 10 }));

  const garagesToDisplay = applyGarageFilters({
    garages: isCustomerCD(collectionDeliveryOption) ? customerCndGarages : nonCustomerCndGarages,
    vehicleWeight: get(booking, 'vehicle.weight', 0),
    existingGarageInvites: booking.garageInvites,
    searchInput: state.garageNameSearchInput,
  });

  const garagesWithSpecialityData = addWorkOperationSupportFlag(garagesToDisplay, state.capableGarages);

  const bookingDoesntHaveAcceptedInvite = !get(booking, 'garageInvites', []).find(({ status }) =>
    ['accepted'].includes(status)
  );
  const specialityCapabilitiesReady = state.specialityCapabilitiesStatus === 'ready';
  const enableSubmit =
    garagesWithSpecialityData.length && bookingDoesntHaveAcceptedInvite && specialityCapabilitiesReady;
  const canLoadMoreGarages = state.count < garagesWithSpecialityData.length - 10;

  const getSubmitDisabledReason = () => {
    if (state.specialityCapabilitiesStatus === 'loading') {
      return 'Loading garage work operation capabilities...';
    }
    if (!garagesWithSpecialityData.length) {
      return 'Loading...';
    }
    return 'Cancel the current accepted invite to select an alternative garage';
  };

  return (
    <StyledModal
      title={
        <>
          Select a garage
          <GarageSearch
            size="small"
            placeholder="Search Garage Name"
            onChange={(e) => {
              e.persist();
              setState((st) => ({ ...st, garageNameSearchInput: e.target.value }));
            }}
          />
        </>
      }
      width={950}
      visible={checkPopupOpen}
      onOk={handleOk}
      onCancel={onCancelled}
      footer={[
        <Button key="cancel" onClick={onCancelled}>
          Cancel
        </Button>,
        enableSubmit ? (
          <Button key="submit" type="primary" onClick={handleOk}>
            Select garage
          </Button>
        ) : (
          <Popover content={getSubmitDisabledReason()}>
            <Button key="disabled" type="primary" disabled style={{ marginLeft: 5 }}>
              Select garage
            </Button>
          </Popover>
        ),
      ]}
    >
      {loadingGarages ? (
        <Loading />
      ) : (
        <>
          <GarageTable
            garages={garagesWithSpecialityData}
            collectionDeliveryOption={collectionDeliveryOption}
            state={state}
            setState={setState}
            onGarageRowClick={onGarageRowClick}
          />
          <OutOfHoursWarning
            garage={state.garage}
            collectionTime={collectionTime}
            deliveryTime={deliveryTime}
          />
          <BottomDiv>
            {canLoadMoreGarages && (
              <Button className="load-more" type="link" onClick={loadMoreGarages}>
                Load more garages
              </Button>
            )}
            <ChangeCndOptions
              state={state}
              onChange={changeCndType}
              collectionDeliveryOption={collectionDeliveryOption}
              isCustomerCnd={isCustomerCD(collectionDeliveryOption)}
            />
          </BottomDiv>
        </>
      )}
    </StyledModal>
  );
}

GaragePickerModal.defaultProps = {
  booking: {},
  garages: [],
  checkPopupOpen: false,
  closePopup: () => {},
  loadingGarages: false,
};

GaragePickerModal.propTypes = {
  booking: PropTypes.object,
  garages: PropTypes.array,
  checkPopupOpen: PropTypes.bool,
  closePopup: PropTypes.func,
  loadingGarages: PropTypes.bool,
};

export default GaragePickerModal;
